import MarkdownContent from "../MarkdownContent";

function ApiKeyDetails({ scheme }) {
  return (
    <>
      <p>
        Include your API key in the {scheme.in === "header" ? "request header" : scheme.in === "query" ? "query parameter" : "cookie"}:
      </p>
      <div className="code-block">
        <div className="code-header">
          <span className="code-language">
            {scheme.in === "header" ? "HTTP Header" : scheme.in === "query" ? "Query Parameter" : "Cookie"}
          </span>
        </div>
        <pre>
          {scheme.in === "header" && `${scheme.name}: YOUR_API_KEY`}
          {scheme.in === "query" && `?${scheme.name}=YOUR_API_KEY`}
          {scheme.in === "cookie" && `${scheme.name}=YOUR_API_KEY`}
        </pre>
      </div>
    </>
  );
}

function OAuthFlows({ flows }) {
  return (
    <>
      <h3>OAuth 2.0 Flows</h3>
      {Object.entries(flows).map(([flowType, flow]) => (
        <div key={flowType} style={{ marginBottom: "20px" }}>
          <h4 style={{ textTransform: "capitalize" }}>{flowType} Flow</h4>
          {flow.authorizationUrl && (
            <p>
              <strong>Authorization URL:</strong> <code>{flow.authorizationUrl}</code>
            </p>
          )}
          {flow.tokenUrl && (
            <p>
              <strong>Token URL:</strong> <code>{flow.tokenUrl}</code>
            </p>
          )}
          {flow.scopes && Object.keys(flow.scopes).length > 0 && (
            <>
              <p>
                <strong>Scopes:</strong>
              </p>
              <ul style={{ paddingLeft: "24px" }}>
                {Object.entries(flow.scopes).map(([scope, description]) => (
                  <li key={scope}>
                    <code>{scope}</code> - {description}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      ))}
    </>
  );
}

export default function AuthenticationSection({ apiSpec }) {
  const securitySchemes = apiSpec.components?.securitySchemes;

  if (!securitySchemes || Object.keys(securitySchemes).length === 0) {
    return (
      <div>
        <div className="content-header">
          <h1>Authentication</h1>
          <p className="subtitle">Secure your API requests with proper authentication</p>
        </div>

        <div className="card">
          <h2 style={{ marginTop: 0 }}>Authentication Required</h2>
          <p>
            This API requires authentication. Please refer to your API provider for authentication
            details and credentials.
          </p>

          <div className="info-box">
            <div className="info-box-title">🔐 Security</div>
            <p>Contact your administrator to obtain the necessary authentication credentials.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="content-header">
        <h1>Authentication</h1>
        <p className="subtitle">Secure your API requests with proper authentication</p>
      </div>

      {Object.entries(securitySchemes).map(([schemeName, scheme]) => (
        <div className="card" key={schemeName}>
          <h2 style={{ marginTop: 0 }}>
            {scheme.type === "http" && scheme.scheme === "bearer" && "Bearer Token Authentication"}
            {scheme.type === "http" && scheme.scheme === "basic" && "Basic Authentication"}
            {scheme.type === "apiKey" && "API Key Authentication"}
            {scheme.type === "oauth2" && "OAuth 2.0 Authentication"}
            {scheme.type === "openIdConnect" && "OpenID Connect Authentication"}
          </h2>

          {scheme.description && <MarkdownContent content={scheme.description} />}

          {scheme.type === "http" && scheme.scheme === "bearer" && (
            <>
              <p>Include your token in the request header:</p>
              <div className="code-block">
                <div className="code-header">
                  <span className="code-language">HTTP Header</span>
                </div>
                <pre>Authorization: Bearer YOUR_TOKEN</pre>
              </div>
            </>
          )}

          {scheme.type === "http" && scheme.scheme === "basic" && (
            <>
              <p>Include your credentials in the request header:</p>
              <div className="code-block">
                <div className="code-header">
                  <span className="code-language">HTTP Header</span>
                </div>
                <pre>Authorization: Basic BASE64_ENCODED_CREDENTIALS</pre>
              </div>
            </>
          )}

          {scheme.type === "apiKey" && <ApiKeyDetails scheme={scheme} />}
          {scheme.type === "oauth2" && scheme.flows && <OAuthFlows flows={scheme.flows} />}

          <div className="info-box">
            <div className="info-box-title">🔐 Keep Your Credentials Secret</div>
            <p>
              Never share your authentication credentials in publicly accessible code repositories or
              client-side code. Contact your administrator to obtain credentials.
            </p>
          </div>
        </div>
      ))}

      <div className="card">
        <h2>Example Request</h2>
        <div className="code-block">
          <div className="code-header">
            <span className="code-language">cURL</span>
          </div>
          <pre>{`curl -X GET "${apiSpec.servers?.[0]?.url || ""}/endpoint" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json"`}</pre>
        </div>
      </div>
    </div>
  );
}
